import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Banner, Page, Layout, Card, BlockStack, Button } from '@shopify/polaris';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Page title="Application Error">
          <Layout>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Banner tone="critical">
                    <h3>⚠️ App Initialization Error</h3>
                    <p>There was an issue initializing the Shopify App Bridge. This usually happens when:</p>
                    <ul>
                      <li>Missing required URL parameters (shop, host)</li>
                      <li>Invalid API key configuration</li>
                      <li>Network connectivity issues</li>
                    </ul>
                    
                    <h4>Troubleshooting Steps:</h4>
                    <ol>
                      <li>Refresh the page</li>
                      <li>Try reinstalling the app from your Partner Dashboard</li>
                      <li>Check that your app URL is correctly configured</li>
                      <li>Contact support if the issue persists</li>
                    </ol>
                  </Banner>
                  
                  <BlockStack gap="200" align="center">
                    <Button variant="primary" onClick={this.handleReload}>
                      🔄 Refresh Page
                    </Button>
                    <Button onClick={this.handleReload}>
                      🔄 Try Again
                    </Button>
                  </BlockStack>
                  
                  {this.state.error && (
                    <Card>
                      <BlockStack gap="200">
                        <h4>Technical Details</h4>
                        <p><strong>Error:</strong> {this.state.error.message}</p>
                        {this.state.errorInfo && (
                          <details style={{ marginTop: '10px' }}>
                            <summary>Stack Trace</summary>
                            <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                              {this.state.errorInfo.componentStack}
                            </pre>
                          </details>
                        )}
                      </BlockStack>
                    </Card>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      );
    }

    return this.props.children;
  }
}
